import { IconNames } from "@blueprintjs/icons";
import SButton from "../global/sbutton";
import { Alert, Intent } from "@blueprintjs/core";
import styles from './news.module.css';
import { useSession } from "next-auth/react";
import { useCallback, useState } from "react";
import { useSocket } from "../global/socket";
import { useToaster } from "../global/toaster";
import { ClientRequests } from "@/src/enum/socket";

type Props = {
    newsPostId: number;
    onDeletePost: (newsPostId: number) => void;
    title: string;
}

export default function HomeNewsPostDeleteButton(
    {
        newsPostId,
        onDeletePost,
        title,
    }: Props,
): JSX.Element {
    const { data: session } = useSession();
    const toaster = useToaster();
    const { socket } = useSocket();

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const onClickButton = useCallback(
        () => {
            setIsAlertOpen(true);
        },
        [],
    );

    const onCloseAlert = useCallback(
        () => {
            setIsAlertOpen(false);
        },
        [],
    );

    const onConfirm = useCallback(
        async () => {
            let success = false;

            try {
                if (!socket) {
                    throw new Error('Socket not connected');
                }

                setIsDeleting(true);

                const err = await socket.emitWithAck(ClientRequests.AdminNewsPostDelete, newsPostId);
                if (err) {
                    throw new Error(err);
                }

                success = true;
            } catch (err) {
                console.error('Failed to delete news post', err);
                toaster.show({
                    message: 'Failed to delete news post',
                    intent: Intent.DANGER,
                });
            } finally {
                setIsDeleting(false);
            }

            if (success) {
                toaster.show({
                    message: 'News Post Deleted',
                    intent: Intent.SUCCESS,
                });

                if (onDeletePost) {
                    onDeletePost(newsPostId);
                }
            }
        },
        [socket, toaster, newsPostId, onDeletePost],
    );

    if (!session?.user?.isAdmin) {
        return null;
    }

    return (
        <div>
            <SButton
                disabled={isDeleting || !socket}
                icon={IconNames.Trash}
                intent={Intent.DANGER}
                onClick={onClickButton}
            />
            <Alert
                cancelButtonText="Cancel"
                confirmButtonText="Delete News Post"
                icon={IconNames.Trash}
                intent={Intent.DANGER}
                isOpen={isAlertOpen}
                onConfirm={onConfirm}
                onClose={onCloseAlert}
            >
                Are you sure you want to delete the news post titled <pre style={{ display: "inline" }}>{title}</pre>? This action cannot be undone.
            </Alert>
        </div>
    );
}
